/**
 * API service layer – typed wrappers around every Supabase query and edge
 * function call referenced in the legacy Wized configuration.
 *
 * Each function returns `{ data, error }` following the Supabase convention.
 * The hooks in `src/hooks/` consume these; UI components should never import
 * this file directly.
 */

import { getAddOnIdentityKey } from "./addOnIdentity";
import { isExpired } from "./helpers";
import { getOrCreateShareGuestId } from "./shareTracking";
import { filterPurchasedCourseVideos } from "./videoFilters";
import { supabase } from "./supabase";
import type { Database } from "./database.types";
import type {
  AdminAddOnRequest,
  AdminCourseAccessExtensionResult,
  AdminCourseAccessTimelineRow,
  AdminShareLeaderboardItem,
  AdminSharingDashboard,
  BundleWithCourses,
  CartItem,
  ContactSubmissionRow,
  CourseWithDetails,
  NewsletterSubscriberRow,
  PlaylistCourseWithDetails,
  ShareResourceType,
  ShareSettings,
  StripeCheckoutResponse,
} from "./schema";

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const COURSE_SHARE_TTL_DAYS = 120;
const PLAYLIST_SHARE_TTL_DAYS = 120;

type NewsFeatureInsert = Database["public"]["Tables"]["news_features"]["Insert"];
type NewsFeatureUpdate = Database["public"]["Tables"]["news_features"]["Update"];

async function getFunctionInvokeErrorMessage(error: unknown) {
  const fallback =
    error instanceof Error ? error.message : "Edge function request failed";
  const context = (error as { context?: unknown } | null)?.context;

  if (
    context &&
    typeof (context as Response).clone === "function" &&
    typeof (context as Response).json === "function"
  ) {
    try {
      const body = await (context as Response).clone().json();
      if (
        body &&
        typeof body === "object" &&
        "error" in body &&
        typeof body.error === "string"
      ) {
        return body.error;
      }
      if (
        body &&
        typeof body === "object" &&
        "message" in body &&
        typeof body.message === "string"
      ) {
        return body.message;
      }
    } catch {
      // Fall back to the Supabase client error message below.
    }
  }

  return fallback;
}

async function invokeFunction<T>(name: string, body?: unknown) {
  const { data, error } = await supabase.functions.invoke(name, {
    body: body as Record<string, unknown> | undefined,
  });

  if (error) {
    return {
      data: null,
      error: { message: await getFunctionInvokeErrorMessage(error) },
    };
  }

  if (
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    "error" in data &&
    typeof data.error === "string"
  ) {
    return { data: null, error: { message: data.error } };
  }

  return { data: (data as T | null) ?? null, error: null };
}

// ---------------------------------------------------------------------------
// Courses
// ---------------------------------------------------------------------------

const COURSE_LIBRARY_SELECT = `
  id,
  name,
  short_description,
  long_description,
  price,
  is_free,
  most_popular,
  total_duration,
  total_videos,
  thumbnail_links_course,
  Instructor:Instructor(name, description),
  preview_video_id (video_url),
  category
`;

const BUNDLE_PUBLIC_SELECT = `
  id,
  name,
  short_description,
  long_description,
  price,
  discount_percentage,
  thumbnail_url,
  is_active,
  sort_order,
  created_at,
  updated_at,
  bundle_courses (
    id,
    order_index,
    course:course_id (
      ${COURSE_LIBRARY_SELECT}
    )
  )
`;

function normalizeBundleRows(rows: any[] | null): BundleWithCourses[] | null {
  return rows?.map((row) => {
    const courses = (row.bundle_courses ?? [])
      .filter((entry: any) => entry.course)
      .sort((left: any, right: any) => Number(left.order_index ?? 0) - Number(right.order_index ?? 0))
      .map((entry: any) => entry.course as CourseWithDetails);
    const discountPercentage = row.discount_percentage == null ? null : Number(row.discount_percentage);

    return {
      id: row.id,
      name: row.name,
      short_description: row.short_description ?? null,
      long_description: row.long_description ?? null,
      price: Number(row.price ?? 0),
      discount_percentage: discountPercentage,
      thumbnail_url: row.thumbnail_url ?? null,
      music_url: row.music_url ?? null,
      is_active: Boolean(row.is_active),
      sort_order: Number(row.sort_order ?? 0),
      created_at: row.created_at,
      updated_at: row.updated_at,
      courses,
    };
  }) ?? null;
}

function normalizeBundleAdminError(error: { message?: string } | null): { message: string } | null {
  if (!error) return null;

  const message = error.message ?? "Something went wrong while loading signature routines.";
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes("discount_percentage") && lowerMessage.includes("bundles")) {
    return {
      message:
        "Your database is missing the new bundles.discount_percentage column. Run the latest Supabase migrations, then reload the page.",
    };
  }

  if (lowerMessage.includes("bundle_music_assets")) {
    return {
      message:
        "Your database is missing the new bundle_music_assets table. Run the latest Supabase migrations, then reload the admin page.",
    };
  }

  if (lowerMessage.includes("relation") && lowerMessage.includes("bundles")) {
    return {
      message:
        "Bundle tables are not installed in this database yet. Run the latest Supabase migration, then reload the admin page.",
    };
  }

  if (lowerMessage.includes("row-level security")) {
    return {
      message:
        "Your account can open the admin page, but the database is rejecting bundle changes. Make sure this user has app_metadata.role = 'admin' in Supabase Auth.",
    };
  }

  return { message };
}

/** Fetch the full course catalog (library page). */
export async function getCourseList() {
  const result = await supabase
    .from("course")
    .select(COURSE_LIBRARY_SELECT);

  return {
    data: result.data as CourseWithDetails[] | null,
    error: result.error,
  };
}

/** Fetch a single course by ID. */
export async function getCourseItem(courseId: string) {
  const result = await supabase
    .from("course")
    .select("*, Instructor:Instructor(name, description), preview_video_id(video_url)")
    .eq("id", courseId)
    .single();

  return {
    data: result.data as CourseWithDetails | null,
    error: result.error,
  };
}

/** Fetch course categories join table (for filters). */
export async function getCourseCategories() {
  return supabase
    .from("course_categories")
    .select("course_id, category_id(name)");
}

/** Fetch all active bundles for the library page. */
export async function getBundleList() {
  const result = await supabase
    .from("bundles")
    .select(BUNDLE_PUBLIC_SELECT)
    .eq("is_active", true)
    .order("name", { ascending: true })
    .order("created_at", { ascending: true });

  return {
    data: normalizeBundleRows(result.data as any[] | null),
    error: normalizeBundleAdminError(result.error),
  };
}

/** Fetch a single active bundle by ID. */
export async function getBundleItem(bundleId: string) {
  const result = await supabase
    .from("bundles")
    .select(BUNDLE_PUBLIC_SELECT)
    .eq("id", bundleId)
    .eq("is_active", true)
    .single();

  return {
    data: normalizeBundleRows(result.data ? [result.data] as any[] : null)?.[0] ?? null,
    error: normalizeBundleAdminError(result.error),
  };
}

/** Fetch all bundles including inactive ones (admin). */
export async function getAdminBundles() {
  const result = await supabase
    .from("bundles")
    .select(BUNDLE_PUBLIC_SELECT)
    .order("name", { ascending: true })
    .order("created_at", { ascending: true });

  if (result.error) {
    return {
      data: null,
      error: normalizeBundleAdminError(result.error),
    };
  }

  const bundles = normalizeBundleRows(result.data as any[] | null) ?? [];
  const bundleIds = bundles.map((bundle) => bundle.id);

  if (bundleIds.length === 0) {
    return { data: bundles, error: null };
  }

  const assetsResult = await supabase
    .from("bundle_music_assets")
    .select("bundle_id, music_url")
    .in("bundle_id", bundleIds);

  if (assetsResult.error) {
    return {
      data: null,
      error: normalizeBundleAdminError(assetsResult.error),
    };
  }

  const musicAssets = (assetsResult.data ?? []) as Array<{
    bundle_id: string;
    music_url: string | null;
  }>;
  const musicUrlByBundleId = new Map(
    musicAssets.map((asset) => [asset.bundle_id, asset.music_url]),
  );

  return {
    data: bundles.map((bundle) => ({
      ...bundle,
      music_url: musicUrlByBundleId.get(bundle.id) ?? null,
    })),
    error: null,
  };
}

/** Create or update a bundle and its ordered course list (admin). */
export async function upsertBundle(bundle: {
  id?: string;
  name: string;
  short_description?: string;
  long_description?: string;
  thumbnail_url?: string;
  music_url?: string | null;
  price: number;
  discount_percentage?: number | null;
  is_active?: boolean;
  sort_order?: number;
  course_ids: string[];
}) {
  const courseIds = Array.from(new Set(bundle.course_ids.filter(Boolean)));

  if (courseIds.length < 2) {
    return {
      data: null,
      error: { message: "Bundles must include at least two tutorials." },
    };
  }

  const payload = {
    name: bundle.name.trim(),
    short_description: bundle.short_description?.trim() || null,
    long_description: bundle.long_description?.trim() || null,
    thumbnail_url: bundle.thumbnail_url?.trim() || null,
    price: bundle.price,
    discount_percentage: bundle.discount_percentage ?? null,
    is_active: bundle.is_active ?? true,
    updated_at: new Date().toISOString(),
    ...(bundle.sort_order !== undefined ? { sort_order: bundle.sort_order } : {}),
  };

  const bundleResult = bundle.id
    ? await supabase.from("bundles").update(payload).eq("id", bundle.id).select().single()
    : await supabase.from("bundles").insert(payload).select().single();

  if (bundleResult.error || !bundleResult.data) {
    return { data: null, error: normalizeBundleAdminError(bundleResult.error) };
  }

  const savedBundleId = bundleResult.data.id;

  if ("music_url" in bundle) {
    const musicUrl = bundle.music_url?.trim() || null;
    const musicAssetResult = musicUrl
      ? await supabase
        .from("bundle_music_assets")
        .upsert(
          {
            bundle_id: savedBundleId,
            music_url: musicUrl,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "bundle_id" },
        )
      : await supabase
        .from("bundle_music_assets")
        .delete()
        .eq("bundle_id", savedBundleId);

    if (musicAssetResult.error) {
      return { data: null, error: normalizeBundleAdminError(musicAssetResult.error) };
    }
  }

  const deleteResult = await supabase
    .from("bundle_courses")
    .delete()
    .eq("bundle_id", savedBundleId);

  if (deleteResult.error) {
    return { data: null, error: normalizeBundleAdminError(deleteResult.error) };
  }

  const insertResult = await supabase.from("bundle_courses").insert(
    courseIds.map((courseId, index) => ({
      bundle_id: savedBundleId,
      course_id: courseId,
      order_index: index,
    })),
  );

  if (insertResult.error) {
    return { data: null, error: normalizeBundleAdminError(insertResult.error) };
  }

  return bundleResult;
}

/** Delete a bundle when unused, otherwise archive it by setting it inactive. */
export async function deleteBundle(bundleId: string) {
  const purchaseCountResult = await supabase
    .from("bundle_purchases")
    .select("id", { count: "exact", head: true })
    .eq("bundle_id", bundleId);

  if (purchaseCountResult.error) {
    return { data: null, error: normalizeBundleAdminError(purchaseCountResult.error) };
  }

  if ((purchaseCountResult.count ?? 0) > 0) {
    const result = await supabase
      .from("bundles")
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq("id", bundleId);

    return { data: result.data, error: normalizeBundleAdminError(result.error) };
  }

  const result = await supabase.from("bundles").delete().eq("id", bundleId);
  return { data: result.data, error: normalizeBundleAdminError(result.error) };
}

// ---------------------------------------------------------------------------
// Videos
// ---------------------------------------------------------------------------

/** Fetch all videos sorted by order_index. */
export async function getVideos() {
  return supabase
    .from("videos")
    .select("*")
    .order("order_index", { ascending: true });
}

/** Fetch videos for a single purchased course, excluding public previews. */
export async function getCourseVideos(courseId: string) {
  const result = await supabase
    .from("videos")
    .select("*")
    .eq("course_id", courseId)
    .order("order_index", { ascending: true });

  return {
    data: filterPurchasedCourseVideos(result.data),
    error: result.error,
  };
}

/** Fetch all video index/timestamp entries. */
export async function getVideoIndex() {
  return supabase.from("video_index").select("*");
}

/** Fetch video index entries for a single course. */
export async function getCourseVideoIndex(courseId: string) {
  return supabase
    .from("video_index")
    .select("*")
    .eq("course_id", courseId)
    .order("id", { ascending: true });
}

// ---------------------------------------------------------------------------
// Course Permissions (user's purchased/enrolled courses)
// ---------------------------------------------------------------------------

/** Fetch the current user's course permissions with course details. */
export async function getCoursePermissions(userId: string) {
  return supabase
    .from("course_permissions")
    .select(
      `
      id,
      user_id,
      expires_at,
      course_id (
        id,
        name,
        price,
        thumbnail_links_course,
        preview_video_id(video_url),
        Instructor:Instructor(name)
      )
    `,
    )
    .eq("user_id", userId);
}

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

/** Fetch course-level progress for the current user. */
export async function getUserCourseProgress() {
  return supabase.from("user_course_progress").select("*");
}

/** Fetch per-video completion status for the current user. */
export async function getUserVideoProgress(userId: string) {
  return supabase
    .from("user_video_progress")
    .select("video_id, completed")
    .eq("user_id", userId);
}

/** Mark a video as completed (upsert). */
export async function markVideoComplete(userId: string, videoId: string) {
  return supabase
    .from("user_video_progress")
    .upsert(
      { user_id: userId, video_id: videoId, completed: true },
      { onConflict: "user_id,video_id" },
    );
}

// ---------------------------------------------------------------------------
// Playlists ("Performances")
// ---------------------------------------------------------------------------

/** Fetch all playlists owned by a user. */
export async function getPlaylists(userId: string) {
  return supabase.from("playlists").select("*").eq("user_id", userId);
}

/** Fetch a single playlist by ID. */
export async function getPlaylistItem(playlistId: string) {
  return supabase.from("playlists").select("*").eq("id", playlistId).single();
}

/** Select fragment for playlist-course rows with joined course info. */
const PLAYLIST_COURSE_SELECT = `
  id,
  order_index,
  notes,
  playlist_id (id, user_id),
  course_id (id, name, thumbnail_links_course, price, preview_video_id(video_url), Instructor(name))
`;

/** Fallback select for environments where the notes column has not been migrated yet. */
const PLAYLIST_COURSE_SELECT_LEGACY = `
  id,
  order_index,
  playlist_id (id, user_id),
  course_id (id, name, thumbnail_links_course, price, preview_video_id(video_url), Instructor(name))
`;

function isMissingPlaylistCourseNotesColumnError(error: { message?: string } | null) {
  const message = error?.message?.toLowerCase() ?? "";
  return message.includes("playlist_courses") && message.includes("notes");
}

function withNullNotes(
  rows: Omit<PlaylistCourseWithDetails, "notes">[] | null,
): PlaylistCourseWithDetails[] | null {
  return rows?.map((row) => ({ ...row, notes: null })) ?? null;
}

/** Fetch all playlist-course entries with full course info. */
export async function getPlaylistCourses() {
  const result = await supabase
    .from("playlist_courses")
    .select(PLAYLIST_COURSE_SELECT)
    .order("order_index", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: true })
    .order("id", { ascending: true });

  if (!isMissingPlaylistCourseNotesColumnError(result.error)) {
    return result;
  }

  const fallback = await supabase
    .from("playlist_courses")
    .select(PLAYLIST_COURSE_SELECT_LEGACY)
    .order("order_index", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: true })
    .order("id", { ascending: true });

  return {
    data: withNullNotes(fallback.data as Omit<PlaylistCourseWithDetails, "notes">[] | null),
    error: fallback.error,
  };
}

/** Fetch playlist-course entries for a single playlist. */
export async function getPlaylistCoursesForPlaylist(playlistId: string) {
  const result = await supabase
    .from("playlist_courses")
    .select(PLAYLIST_COURSE_SELECT)
    .eq("playlist_id", playlistId)
    .order("order_index", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: true })
    .order("id", { ascending: true });

  if (!isMissingPlaylistCourseNotesColumnError(result.error)) {
    return result;
  }

  const fallback = await supabase
    .from("playlist_courses")
    .select(PLAYLIST_COURSE_SELECT_LEGACY)
    .eq("playlist_id", playlistId)
    .order("order_index", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: true })
    .order("id", { ascending: true });

  return {
    data: withNullNotes(fallback.data as Omit<PlaylistCourseWithDetails, "notes">[] | null),
    error: fallback.error,
  };
}

/**
 * Fetch playlist courses with video-level detail (used on shared playlist
 * and course-shared pages).
 */
export async function getPlaylistCoursesVideos() {
  return supabase
    .from("playlist_courses")
    .select(
      `
      playlist_id (id, user_id),
      course_id (id, name, preview_video_id(video_url))
    `,
    )
    .order("order_index", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: true })
    .order("id", { ascending: true });
}

/** Create a new playlist. */
export async function createPlaylist(userId: string, name: string) {
  return supabase
    .from("playlists")
    .insert({ user_id: userId, name })
    .select()
    .single();
}

/** Rename a playlist. */
export async function updatePlaylistName(userId: string, playlistId: string, name: string) {
  const trimmed = name.trim();
  if (!trimmed) {
    return { data: null, error: { message: "Performance name is required" } };
  }

  return supabase
    .from("playlists")
    .update({ name: trimmed })
    .eq("id", playlistId)
    .eq("user_id", userId)
    .select()
    .single();
}

/** Delete a playlist. */
export async function deletePlaylist(playlistId: string) {
  return supabase.from("playlists").delete().eq("id", playlistId);
}

async function getNextPlaylistCourseOrderIndex(playlistId: string) {
  const result = await supabase
    .from("playlist_courses")
    .select("order_index")
    .eq("playlist_id", playlistId)
    .not("order_index", "is", null)
    .order("order_index", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (result.error) {
    return { data: null, error: result.error };
  }

  return { data: (result.data?.order_index ?? 0) + 1, error: null };
}

/** Add a course to a playlist. */
export async function addCourseToPlaylist(
  playlistId: string,
  courseId: string,
) {
  const nextOrderResult = await getNextPlaylistCourseOrderIndex(playlistId);
  if (nextOrderResult.error || nextOrderResult.data == null) {
    return { data: null, error: nextOrderResult.error ?? { message: "Could not determine playlist order" } };
  }

  return supabase
    .from("playlist_courses")
    .insert({
      playlist_id: playlistId,
      course_id: courseId,
      order_index: nextOrderResult.data,
    })
    .select()
    .single();
}

/** Update the notes on a playlist-course entry. */
export async function updatePlaylistCourseNotes(
  playlistCourseId: string,
  notes: string | null,
) {
  return supabase
    .from("playlist_courses")
    .update({ notes })
    .eq("id", playlistCourseId);
}

/** Remove a single course from a playlist. */
export async function removeCourseFromPlaylist(playlistCourseId: string) {
  return supabase.from("playlist_courses").delete().eq("id", playlistCourseId);
}

/** Reorder courses within a playlist via edge function. */
export async function updatePlaylistOrder(
  playlistId: string,
  courseIds: string[],
) {
  return supabase.functions.invoke("update_playlist_order", {
    body: { playlist_id: playlistId, course_ids: courseIds },
  });
}

// ---------------------------------------------------------------------------
// Sharing – Courses
// ---------------------------------------------------------------------------

/** Fetch the current user's course share records. */
export async function getCourseShares(userId: string) {
  return supabase
    .from("course_shares")
    .select("*")
    .eq("shared_by", userId)
    .order("shared_at", { ascending: false });
}

async function getActiveCoursePermission(userId: string, courseId: string) {
  const result = await supabase
    .from("course_permissions")
    .select("id, expires_at, is_active, created_at")
    .eq("user_id", userId)
    .eq("course_id", courseId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (result.error || !result.data) {
    return { data: null, error: result.error };
  }

  if (result.data.is_active === false) {
    return { data: null, error: { message: "Course access is inactive" } };
  }

  if (result.data.expires_at && isExpired(result.data.expires_at)) {
    return { data: null, error: { message: "Course access has expired" } };
  }

  return { data: result.data, error: null };
}

async function getLatestActiveCourseShare(userId: string, courseId: string) {
  const result = await supabase
    .from("course_shares")
    .select("*")
    .eq("shared_by", userId)
    .eq("course_id", courseId)
    .order("shared_at", { ascending: false });

  if (result.error || !result.data) {
    return { data: null, error: result.error };
  }

  const activeShare = (result.data as Database["public"]["Tables"]["course_shares"]["Row"][]).find(
    (share) => !share.expires_at || !isExpired(share.expires_at),
  );

  return { data: activeShare ?? null, error: null };
}

async function getLatestActivePlaylistShare(userId: string, playlistId: string) {
  const result = await supabase
    .from("playlist_shares")
    .select("*")
    .eq("shared_by", userId)
    .eq("playlist_id", playlistId)
    .order("shared_at", { ascending: false });

  if (result.error || !result.data) {
    return { data: null, error: result.error };
  }

  const activeShare = (result.data as Database["public"]["Tables"]["playlist_shares"]["Row"][]).find(
    (share) => !share.expires_at || !isExpired(share.expires_at),
  );

  return { data: activeShare ?? null, error: null };
}

/** Resolve a share token to its course_id (for public shared pages). */
export async function resolveCourseShareToken(shareToken: string) {
  const shareResult = await supabase
    .from("course_shares")
    .select("course_id, shared_by, expires_at")
    .eq("share_token", shareToken)
    .single();

  if (shareResult.error || !shareResult.data) {
    return {
      data: null,
      error: { message: shareResult.error?.message ?? "Invalid or unknown share link" },
    };
  }

  const share = shareResult.data;
  if (!share.course_id || !share.shared_by) {
    return { data: null, error: { message: "Invalid or unknown share link" } };
  }

  if (share.expires_at && isExpired(share.expires_at)) {
    return { data: null, error: { message: "This share link has expired" } };
  }

  const permissionResult = await getActiveCoursePermission(
    share.shared_by,
    share.course_id,
  );

  if (permissionResult.error || !permissionResult.data) {
    return {
      data: null,
      error: { message: "This share link is no longer available" },
    };
  }

  return { data: { course_id: share.course_id }, error: null };
}

/** Resolve a playlist invitation token (for public shared pages). */
export async function resolvePlaylistShareToken(invitationToken: string) {
  return supabase
    .from("playlist_shares")
    .select("invitation_token, expires_at, playlist_id(id, name, user_id, music_url)")
    .eq("invitation_token", invitationToken)
    .single();
}

/** Update the music_url on a playlist. Pass null to remove. */
export async function updatePlaylistMusicUrl(
  playlistId: string,
  musicUrl: string | null,
) {
  if (musicUrl !== null) {
    try {
      const parsed = new URL(musicUrl);
      if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
        return { data: null, error: { message: "Only http/https URLs are allowed" } };
      }
    } catch {
      return { data: null, error: { message: "Invalid URL" } };
    }
  }
  return supabase
    .from("playlists")
    .update({ music_url: musicUrl })
    .eq("id", playlistId)
    .select()
    .single();
}

/** Create a share token for a course. */
export async function createCourseShare(userId: string, courseId: string) {
  const permissionResult = await getActiveCoursePermission(userId, courseId);
  if (permissionResult.error || !permissionResult.data) {
    return {
      data: null,
      error: { message: "You can only share tutorials with active access" },
    };
  }

  const existingShareResult = await getLatestActiveCourseShare(userId, courseId);
  if (existingShareResult.error) {
    return { data: null, error: existingShareResult.error };
  }

  const expiresAt = new Date(Date.now() + COURSE_SHARE_TTL_DAYS * DAY_IN_MS).toISOString();

  if (existingShareResult.data) {
    if (!existingShareResult.data.expires_at) {
      return { data: existingShareResult.data, error: null };
    }

    return supabase
      .from("course_shares")
      .update({ expires_at: expiresAt })
      .eq("id", existingShareResult.data.id)
      .select()
      .single();
  }

  return supabase
    .from("course_shares")
    .insert({
      shared_by: userId,
      course_id: courseId,
      expires_at: expiresAt,
    })
    .select()
    .single();
}

// ---------------------------------------------------------------------------
// Sharing – Playlists
// ---------------------------------------------------------------------------

/** Fetch the current user's playlist share records. */
export async function getPlaylistShares(userId: string) {
  return supabase
    .from("playlist_shares")
    .select("*")
    .eq("shared_by", userId)
    .order("shared_at", { ascending: false });
}

/** Fetch shared playlist data with playlist name (for shared-playlist page). */
export async function getSharedPlaylistData() {
  return supabase
    .from("playlist_shares")
    .select("invitation_token, expires_at, playlist_id(id, name, user_id)");
}

/** Create a share invitation for a playlist. */
export async function createPlaylistShare(
  userId: string,
  playlistId: string,
) {
  const expiresAt = new Date(Date.now() + PLAYLIST_SHARE_TTL_DAYS * DAY_IN_MS).toISOString();
  const existingShareResult = await getLatestActivePlaylistShare(userId, playlistId);
  if (existingShareResult.error) {
    return { data: null, error: existingShareResult.error };
  }

  if (existingShareResult.data) {
    if (!existingShareResult.data.expires_at) {
      return { data: existingShareResult.data, error: null };
    }

    return supabase
      .from("playlist_shares")
      .update({ expires_at: expiresAt })
      .eq("id", existingShareResult.data.id)
      .select()
      .single();
  }

  return supabase
    .from("playlist_shares")
    .insert({
      shared_by: userId,
      playlist_id: playlistId,
      expires_at: expiresAt,
    })
    .select()
    .single();
}

// ---------------------------------------------------------------------------
// Comments (playlist-level or course-level)
// ---------------------------------------------------------------------------

const COMMENT_SELECT = "id, playlist_id, course_id, user_id, display_name, body, created_at, pinned_at, updated_at";

/** Fetch comments by playlist or course — pinned first, then chronological. */
export async function getComments(filter: { playlistId?: string; courseId?: string }) {
  let query = supabase
    .from("playlist_comments")
    .select(COMMENT_SELECT)
    .order("pinned_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: true });
  if (filter.playlistId) query = query.eq("playlist_id", filter.playlistId);
  if (filter.courseId) query = query.eq("course_id", filter.courseId);
  return query;
}

/** Create an authenticated comment. */
export async function createComment(
  context: { playlistId?: string; courseId?: string },
  userId: string,
  displayName: string,
  body: string,
) {
  return supabase
    .from("playlist_comments")
    .insert({
      playlist_id: context.playlistId ?? null,
      course_id: context.courseId ?? null,
      user_id: userId,
      display_name: displayName,
      body,
    })
    .select(COMMENT_SELECT)
    .single();
}

/** Create a guest comment via edge function (unauthenticated). */
export async function createGuestComment(
  token: string,
  tokenType: "playlist" | "course",
  displayName: string,
  body: string,
) {
  const { data, error } = await supabase.functions.invoke(
    "add_guest_comment",
    {
      body: {
        ...(tokenType === "playlist"
          ? { invitation_token: token }
          : { share_token: token }),
        display_name: displayName,
        body,
      },
    },
  );
  if (error) {
    return { data: null, error: { message: error.message } };
  }
  return { data: data?.comment ?? data, error: null };
}

/** Update a comment's body (own comments only – RLS enforces). */
export async function updateComment(commentId: string, body: string) {
  return supabase
    .from("playlist_comments")
    .update({ body, updated_at: new Date().toISOString() })
    .eq("id", commentId)
    .select(COMMENT_SELECT)
    .single();
}

/** Pin or unpin a comment (playlist owner only – RLS enforces). */
export async function togglePinComment(commentId: string, pinned: boolean) {
  return supabase
    .from("playlist_comments")
    .update({ pinned_at: pinned ? new Date().toISOString() : null })
    .eq("id", commentId)
    .select(COMMENT_SELECT)
    .single();
}

/** Delete a comment (organizer only – RLS enforces ownership). */
export async function deleteComment(commentId: string) {
  return supabase.from("playlist_comments").delete().eq("id", commentId);
}

// ---------------------------------------------------------------------------
// Share Tracking / Admin Sharing
// ---------------------------------------------------------------------------

/** Track a guest-only shared-link open. Authenticated requests are ignored server-side. */
export async function trackShareOpen(shareType: ShareResourceType, token: string) {
  return invokeFunction<{
    tracked: boolean;
    unique_guest_opens?: number;
    reason?: string;
  }>("track_share_open", {
    shareType,
    token,
    guestId: getOrCreateShareGuestId() ?? undefined,
  });
}

const DEFAULT_SHARE_WARNING_THRESHOLD = 50;
const SHARE_SETTINGS_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ShareSettingsRow = Pick<
  Database["public"]["Tables"]["share_settings"]["Row"],
  "warning_threshold" | "notification_email" | "updated_at"
>;

type PlaylistShareStatsRow = Pick<
  Database["public"]["Tables"]["playlist_share_stats"]["Row"],
  "playlist_id" | "unique_guest_opens" | "last_opened_at" | "last_warned_at" | "warned_count"
> & {
  playlists:
    | Pick<Database["public"]["Tables"]["playlists"]["Row"], "id" | "name">
    | Pick<Database["public"]["Tables"]["playlists"]["Row"], "id" | "name">[]
    | null;
};

type CourseShareStatsRow = Pick<
  Database["public"]["Tables"]["course_share_stats"]["Row"],
  "course_id" | "unique_guest_opens" | "last_opened_at" | "last_warned_at" | "warned_count"
> & {
  course:
    | Pick<Database["public"]["Tables"]["course"]["Row"], "id" | "name">
    | Pick<Database["public"]["Tables"]["course"]["Row"], "id" | "name">[]
    | null;
};

function getJoinedRecord<T>(value: T | T[] | null | undefined) {
  return Array.isArray(value) ? value[0] ?? null : value ?? null;
}

function normalizeShareSettings(row: ShareSettingsRow | null): ShareSettings {
  return {
    warning_threshold: row?.warning_threshold ?? DEFAULT_SHARE_WARNING_THRESHOLD,
    notification_email: row?.notification_email ?? null,
    updated_at: row?.updated_at ?? null,
  };
}

function mapPlaylistShareStats(
  rows: PlaylistShareStatsRow[] | null,
  warningThreshold: number,
): AdminShareLeaderboardItem[] {
  return (rows ?? []).map((row) => {
    const playlist = getJoinedRecord(row.playlists);

    return {
      resource_id: row.playlist_id,
      resource_type: "playlist",
      title: playlist?.name ?? "Untitled",
      unique_guest_opens: row.unique_guest_opens ?? 0,
      last_opened_at: row.last_opened_at ?? null,
      last_warned_at: row.last_warned_at ?? null,
      warned_count: row.warned_count ?? 0,
      over_threshold: (row.unique_guest_opens ?? 0) >= warningThreshold,
    };
  });
}

function mapCourseShareStats(
  rows: CourseShareStatsRow[] | null,
  warningThreshold: number,
): AdminShareLeaderboardItem[] {
  return (rows ?? []).map((row) => {
    const course = getJoinedRecord(row.course);

    return {
      resource_id: row.course_id,
      resource_type: "course",
      title: course?.name ?? "Untitled",
      unique_guest_opens: row.unique_guest_opens ?? 0,
      last_opened_at: row.last_opened_at ?? null,
      last_warned_at: row.last_warned_at ?? null,
      warned_count: row.warned_count ?? 0,
      over_threshold: (row.unique_guest_opens ?? 0) >= warningThreshold,
    };
  });
}

/** Fetch sharing settings and top shared performances/tutorials for the admin tab. */
export async function getAdminSharingDashboard() {
  const [settingsResult, playlistResult, courseResult] = await Promise.all([
    supabase
      .from("share_settings")
      .select("warning_threshold, notification_email, updated_at")
      .limit(1)
      .maybeSingle(),
    supabase
      .from("playlist_share_stats")
      .select(
        `
          playlist_id,
          unique_guest_opens,
          last_opened_at,
          last_warned_at,
          warned_count,
          playlists(id, name)
        `,
      )
      .order("unique_guest_opens", { ascending: false })
      .order("last_opened_at", { ascending: false })
      .limit(10),
    supabase
      .from("course_share_stats")
      .select(
        `
          course_id,
          unique_guest_opens,
          last_opened_at,
          last_warned_at,
          warned_count,
          course(id, name)
        `,
      )
      .order("unique_guest_opens", { ascending: false })
      .order("last_opened_at", { ascending: false })
      .limit(10),
  ]);

  const firstError =
    settingsResult.error ??
    playlistResult.error ??
    courseResult.error;

  if (firstError) {
    return { data: null, error: { message: firstError.message } };
  }

  const settings = normalizeShareSettings(settingsResult.data as ShareSettingsRow | null);

  return {
    data: {
      settings,
      top_playlists: mapPlaylistShareStats(
        playlistResult.data as PlaylistShareStatsRow[] | null,
        settings.warning_threshold,
      ),
      top_courses: mapCourseShareStats(
        courseResult.data as CourseShareStatsRow[] | null,
        settings.warning_threshold,
      ),
    } satisfies AdminSharingDashboard,
    error: null,
  };
}

/** Save admin-managed sharing settings. */
export async function updateAdminShareSettings(settings: {
  warning_threshold: number;
  notification_email: string | null;
}) {
  const warningThreshold = Number(settings.warning_threshold);
  if (!Number.isFinite(warningThreshold) || warningThreshold < 1) {
    return {
      data: null,
      error: { message: "warning_threshold must be a positive number" },
    };
  }

  const notificationEmail = settings.notification_email?.trim().toLowerCase() || null;
  if (notificationEmail && !SHARE_SETTINGS_EMAIL_PATTERN.test(notificationEmail)) {
    return {
      data: null,
      error: { message: "notification_email must be a valid email address" },
    };
  }

  const { data, error } = await supabase
    .from("share_settings")
    .upsert(
      {
        id: true,
        warning_threshold: Math.floor(warningThreshold),
        notification_email: notificationEmail,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "id" },
    )
    .select("warning_threshold, notification_email, updated_at")
    .single();

  if (error) {
    return { data: null, error: { message: error.message } };
  }

  return {
    data: normalizeShareSettings(data as ShareSettingsRow),
    error: null,
  };
}

// ---------------------------------------------------------------------------
// Add-Ons
// ---------------------------------------------------------------------------

/** Hardcoded defaults matching the seeded DB rows – used as initial state so
 *  pages render instantly without waiting for the network. */
export const DEFAULT_VIRTUAL_PRACTICE_ADD_ON = {
  id: "85a62916-a66a-4f18-8219-a90c6e5a1e7b",
  name: "Virtual Practice",
  description:
    "Book a live 1-on-1 session with your instructor to review choreography, get real-time feedback, and feel performance-ready",
  price: 125,
  price_label: "(Per Hour)",
  image_url:
    "https://cdn.prod.website-files.com/686229256a807206c1874545/688c6c3094d49b67d83733a1_Frame%202147226451%20(6).avif",
  booking_url: "https://cal.com/bollyrhythm/virtual-practice",
  is_active: true,
  sort_order: 1,
  created_at: null,
};

export const DEFAULT_CUSTOM_MIX_ADD_ON = {
  id: "e55392fa-2415-4570-bdd8-1be7313cd03c",
  name: "Custom Mix",
  description:
    "Let us handle the music! We'll create a professional, polished audio mix using the songs in your performance so everything flows seamlessly.",
  price: 59,
  price_label: "(One time payment)",
  image_url:
    "https://cdn.prod.website-files.com/686229256a807206c1874545/688c6c2910d8b8b95b7baa5c_Frame%202147226451%20(7).avif",
  booking_url: "https://cal.com/bollyrhythm/custom-mix",
  is_active: true,
  sort_order: 2,
  created_at: null,
};

export const DEFAULT_ADD_ONS: {
  id: string;
  name: string;
  description: string | null;
  price: number;
  price_label: string | null;
  image_url: string | null;
  booking_url: string | null;
  is_active: boolean | null;
  sort_order: number | null;
  created_at: string | null;
}[] = [
  DEFAULT_VIRTUAL_PRACTICE_ADD_ON,
  DEFAULT_CUSTOM_MIX_ADD_ON,
];

type AddOnPriceRow = { name?: string | null; price: number };

function normalizeCurrentAddOnPricing<T extends AddOnPriceRow>(addOn: T): T {
  if (getAddOnIdentityKey(addOn) === "name:custom mix" && addOn.price !== DEFAULT_CUSTOM_MIX_ADD_ON.price) {
    return { ...addOn, price: DEFAULT_CUSTOM_MIX_ADD_ON.price };
  }

  return addOn;
}

/** Fetch active add-ons ordered by sort_order. */
export async function getAddOns() {
  const response = await supabase
    .from("add_ons")
    .select("*")
    .eq("is_active", true)
    .order("sort_order", { ascending: true });

  if (!response.data) {
    return response;
  }

  return {
    ...response,
    data: response.data.map(normalizeCurrentAddOnPricing),
  };
}

/** Fetch the current user's purchased add-ons with add-on details. */
export async function getAddOnPurchases(userId: string) {
  return supabase
    .from("add_on_purchases")
    .select(`
      id,
      price_paid,
      created_at,
      add_on_id (id, name, description, price, price_label, image_url, booking_url),
      purchase_id!inner (user_id)
    `)
    .eq("purchase_id.user_id", userId)
    .order("created_at", { ascending: false });
}

/** Fetch all add-on purchase records for the admin panel. */
export async function getAdminAddOnRequests() {
  const result = await supabase
    .from("add_on_purchases")
    .select(`
      id,
      price_paid,
      created_at,
      add_on_id (id, name, price_label),
      purchase_id (id, user_id, payment_intent_id, amount_total, currency, status, created_at)
    `)
    .order("created_at", { ascending: false });

  const rows = (result.data ?? []) as unknown as AdminAddOnRequest[];

  // Resolve user IDs to display names via admin RPC
  const userIds = [
    ...new Set(
      rows
        .map((r) => r.purchase_id?.user_id)
        .filter((id): id is string => Boolean(id)),
    ),
  ];

  let userMap: Record<string, { display_name: string; email: string }> = {};
  if (userIds.length > 0) {
    const { data: users } = await supabase.rpc(
      "get_user_display_info" as never,
      { user_ids: userIds } as never,
    );
    if (users) {
      for (const u of users as unknown as { id: string; display_name: string; email: string }[]) {
        userMap[u.id] = { display_name: u.display_name, email: u.email };
      }
    }
  }

  // Attach buyer_name and buyer_email to each row
  const enriched = rows.map((r) => {
    const info = r.purchase_id?.user_id ? userMap[r.purchase_id.user_id] : null;
    return {
      ...r,
      buyer_name: info?.display_name ?? null,
      buyer_email: info?.email ?? null,
    };
  });

  return {
    data: enriched.length > 0 ? enriched : null,
    error: result.error,
  };
}

// ---------------------------------------------------------------------------
// Checkout (Stripe via edge functions)
// ---------------------------------------------------------------------------

/** Create a Stripe checkout session for cart items. */
export async function createStripeCheckout(
  products: CartItem[],
  successUrl: string,
  cancelUrl: string,
) {
  return supabase.functions.invoke<StripeCheckoutResponse>(
    "create_stripe_checkout",
    {
      body: {
        products,
        success_url: successUrl,
        cancel_url: cancelUrl,
      },
    },
  );
}

/** Create a Stripe renewal/extension checkout for an existing permission. */
export async function createStripeRenewal(
  permissionId: string,
  successUrl: string,
  cancelUrl: string,
) {
  return supabase.functions.invoke<StripeCheckoutResponse>(
    "create_stripe_renewal",
    {
      body: {
        permission_id: permissionId,
        success_url: successUrl,
        cancel_url: cancelUrl,
      },
    },
  );
}

// ---------------------------------------------------------------------------
// Free course enrollment
// ---------------------------------------------------------------------------

/** Add a free course to the user's dashboard. */
export async function addFreeCoursePermission(
  courseId: string,
) {
  return supabase.functions.invoke("add_course_to_dashboard", {
    body: { course_id: courseId },
  });
}

export type AdminCourseDashboardGrant = {
  success: boolean;
  existed: boolean;
  updated: boolean;
  user_id: string;
  course_id: string;
  permission: Database["public"]["Tables"]["course_permissions"]["Row"];
  inserted?: Database["public"]["Tables"]["course_permissions"]["Row"];
};

/** Admin-only: add any tutorial to the current admin's dashboard without checkout. */
export async function addAdminCourseToDashboard(courseId: string) {
  const { data, error } = await supabase.rpc(
    "admin_add_course_to_dashboard" as never,
    { p_course_id: courseId } as never,
  );

  if (error) {
    return { data: null, error: { message: error.message } };
  }

  return {
    data: data as AdminCourseDashboardGrant | null,
    error: null,
  };
}

/** Admin-only: fetch latest tutorial access rows with learner display info. */
export async function getAdminCourseAccessTimeline() {
  const { data, error } = await supabase.rpc(
    "admin_get_course_access_timeline" as never,
  );

  if (error) {
    return { data: null, error: { message: error.message } };
  }

  return {
    data: data as AdminCourseAccessTimelineRow[] | null,
    error: null,
  };
}

/** Admin-only: extend an existing learner's tutorial access without checkout. */
export async function extendAdminCourseAccess(permissionId: string, days: number) {
  const { data, error } = await supabase.rpc(
    "admin_extend_course_access" as never,
    { p_permission_id: permissionId, p_extend_days: days } as never,
  );

  if (error) {
    return { data: null, error: { message: error.message } };
  }

  return {
    data: data as AdminCourseAccessExtensionResult | null,
    error: null,
  };
}

// ---------------------------------------------------------------------------
// CMS – Blogs
// ---------------------------------------------------------------------------

/** Fetch all published blogs (public). */
export async function getBlogs() {
  return supabase
    .from("blogs")
    .select("*")
    .eq("is_published", true)
    .order("sort_order", { ascending: true });
}

/** Fetch a single blog by slug (public). */
export async function getBlogBySlug(slug: string) {
  return supabase
    .from("blogs")
    .select("*")
    .eq("slug", slug)
    .eq("is_published", true)
    .single();
}

/** Fetch all blogs including drafts (admin). */
export async function getAdminBlogs() {
  return supabase
    .from("blogs")
    .select("*")
    .order("sort_order", { ascending: true });
}

/** Create a new blog post (admin). */
export async function createBlog(blog: {
  slug: string;
  title: string;
  category: string;
  published_at?: string;
  listing_date?: string;
  read_time?: string;
  excerpt?: string;
  hero_image?: string;
  listing_image?: string;
  author_name?: string;
  author_image?: string;
  author_bio?: string;
  body_html: string;
  is_published?: boolean;
  sort_order?: number;
}) {
  return supabase.from("blogs").insert(blog).select().single();
}

/** Update an existing blog post (admin). */
export async function updateBlog(
  id: string,
  updates: Partial<{
    slug: string;
    title: string;
    category: string;
    published_at: string;
    listing_date: string;
    read_time: string;
    excerpt: string;
    hero_image: string;
    listing_image: string;
    author_name: string;
    author_image: string;
    author_bio: string;
    body_html: string;
    is_published: boolean;
    sort_order: number;
  }>,
) {
  return supabase
    .from("blogs")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();
}

/** Delete a blog post (admin). */
export async function deleteBlog(id: string) {
  return supabase.from("blogs").delete().eq("id", id);
}

// ---------------------------------------------------------------------------
// CMS – News & Features
// ---------------------------------------------------------------------------

/** Fetch all published News & Features items for the Our Story page (public). */
export async function getNewsFeatures() {
  return supabase
    .from("news_features")
    .select("*")
    .eq("is_published", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });
}

/** Fetch all News & Features items including drafts (admin). */
export async function getAdminNewsFeatures() {
  return supabase
    .from("news_features")
    .select("*")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });
}

/** Create a News & Features item (admin). */
export async function createNewsFeature(feature: NewsFeatureInsert) {
  return supabase.from("news_features").insert(feature).select().single();
}

/** Update a News & Features item (admin). */
export async function updateNewsFeature(id: string, updates: NewsFeatureUpdate) {
  return supabase
    .from("news_features")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();
}

/** Delete a News & Features item (admin). */
export async function deleteNewsFeature(id: string) {
  return supabase.from("news_features").delete().eq("id", id);
}

// ---------------------------------------------------------------------------
// CMS – Announcement Banners
// ---------------------------------------------------------------------------

/** Fetch the newest active banner (public). The homepage displays one banner at a time. */
export async function getActiveBanner() {
  return supabase
    .from("announcement_banners")
    .select("*")
    .eq("is_active", true)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
}

/** Fetch all banners (admin). */
export async function getBanners() {
  return supabase
    .from("announcement_banners")
    .select("*")
    .order("created_at", { ascending: false });
}

/** Create or update a banner (admin). */
export async function upsertBanner(banner: {
  id?: string;
  text: string;
  link_text?: string;
  link_url?: string;
  is_active?: boolean;
}) {
  if (banner.id) {
    return supabase
      .from("announcement_banners")
      .update(banner)
      .eq("id", banner.id)
      .select()
      .single();
  }
  return supabase
    .from("announcement_banners")
    .insert(banner)
    .select()
    .single();
}

/** Delete a banner (admin). */
export async function deleteBanner(id: string) {
  return supabase.from("announcement_banners").delete().eq("id", id);
}

// ---------------------------------------------------------------------------
// CMS – Homepage Videos
// ---------------------------------------------------------------------------

/** Fetch all homepage video slots (public). */
export async function getHomepageVideos() {
  return supabase
    .from("homepage_videos")
    .select("*")
    .order("slot", { ascending: true });
}

// ---------------------------------------------------------------------------
// Newsletter
// ---------------------------------------------------------------------------

/** Fetch newsletter subscribers for the admin panel. */
export async function getAdminNewsletterSubscribers() {
  const result = await supabase
    .from("newsletter_subscribers")
    .select("*")
    .order("subscribed_at", { ascending: false });

  return {
    data: (result.data ?? null) as NewsletterSubscriberRow[] | null,
    error: result.error,
  };
}

/** Fetch contact form submissions for the admin panel. */
export async function getAdminContactSubmissions() {
  const result = await supabase
    .from("contact_submissions")
    .select("*")
    .order("submitted_at", { ascending: false });

  return {
    data: (result.data ?? null) as ContactSubmissionRow[] | null,
    error: result.error,
  };
}

/** Subscribe an email to the newsletter (public, no auth). */
export async function subscribeNewsletter(email: string, captchaToken: string) {
  return invokeFunction<{ success: true; alreadySubscribed?: boolean }>("newsletter_subscribe", {
    email,
    captchaToken,
  });
}

/** Submit a contact form (public, no auth). */
export async function submitContactForm(data: {
  name: string;
  email: string;
  subject?: string;
  message: string;
}, captchaToken: string) {
  return invokeFunction<{ success: true }>("contact_submit", {
    ...data,
    captchaToken,
  });
}

/** Update a homepage video slot (admin). */
export async function updateHomepageVideo(
  slot: number,
  data: { poster_url?: string; video_url?: string; mobile_video_url?: string | null },
) {
  return supabase
    .from("homepage_videos")
    .update(data)
    .eq("slot", slot)
    .select()
    .single();
}
